import { useQuery } from "@tanstack/react-query";
import axios from "axios";

interface VoiceConfigQueryKey extends Array<any> {
    0: string;
    1: {
        baseUrl: string;
        user_id: string;
        agent_id: string;
        tenant_id: string;
        workspace_id: string;
    };
}

interface AgentVariableResponseItem {
    key?: string;
}

const mergeVariableKeys = (
    target: Record<string, unknown>,
    variables: AgentVariableResponseItem[] | undefined
) => {
    if (!Array.isArray(variables)) {
        return;
    }

    variables.forEach((item) => {
        if (!item?.key) {
            return;
        }

        if (!(item.key in target)) {
            target[item.key] = null;
        }
    });
};

const fetchDynamicVariables = async ({ queryKey }: { queryKey: VoiceConfigQueryKey }) => {
    const voiceConfig = queryKey[1];

    const commonHeaders = {
        "Content-Type": "application/json",
        "user-id": voiceConfig.user_id.toString(),
        "tenant-id": voiceConfig.tenant_id.toString(),
        "workspace-id": voiceConfig.workspace_id.toString()
    };

    const [dynamicVariablesResult, agentVariablesResult, squadVariablesResult] = await Promise.allSettled([
        axios.get(
            `${voiceConfig.baseUrl}/voice/agent-config/${voiceConfig.agent_id}/dynamic-variables`,
            {
                headers: commonHeaders,
            }
        ),
        axios.get(
            `${voiceConfig.baseUrl}/agent-variable/?agent_id=${voiceConfig.agent_id}&type=input`,
            {
                headers: {
                    accept: "application/json",
                    "workspace-id": voiceConfig.workspace_id.toString()
                }
            }
        ),
        axios.get(
            `${voiceConfig.baseUrl}/agent-variable/squad/${voiceConfig.agent_id}?type=input`,
            {
                headers: {
                    accept: "application/json",
                    "workspace-id": voiceConfig.workspace_id.toString()
                }
            }
        )
    ]);

    if (dynamicVariablesResult.status !== "fulfilled") {
        throw dynamicVariablesResult.reason;
    }

    const filteredData = dynamicVariablesResult.value?.data
        ? { ...dynamicVariablesResult.value.data }
        : {};
    delete filteredData.previous_conversation_summary;
    delete filteredData.last_interaction;

    if (agentVariablesResult.status === "fulfilled") {
        mergeVariableKeys(filteredData, agentVariablesResult.value.data);
    }

    if (squadVariablesResult.status === "fulfilled") {
        mergeVariableKeys(filteredData, squadVariablesResult.value.data);
    }

    return filteredData;
};

export const useDynamicVariablesQuery = (voiceConfig: VoiceConfigQueryKey[1], isOpen: boolean) => {
    return useQuery({
        queryKey: ["dynamicVariables", voiceConfig],
        queryFn: fetchDynamicVariables,
        enabled: isOpen,
        retry: 0
    });
};
